import { TextField, Tooltip } from '@ads/components-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { ParcelFormValues } from '../../../hooks/useParcelFormSchema';
import {
  formatCurrencyInput,
  formatPercentageInput,
  parseCurrencyInput,
  parsePercentageInput,
} from '../../../utils/maskedNumberInput';

/** Campos numéricos dos formulários de parcela. */
export type ParcelNumericFieldName =
  | 'total'
  | 'numberParcel'
  | 'discountPunctualPercentage'
  | 'retroactiveDiscount'
  | 'totalDiscount'
  | 'fine'
  | 'amountOfParcel'
  | 'serial'
  | 'fractionOfParcel';

/** Natureza do valor — decide máscara e leitura do que foi digitado. */
export type ParcelNumericFieldKind = 'currency' | 'percentage' | 'integer';

/** Props de `ParcelNumericField`. */
export interface ParcelNumericFieldProps {
  readonly control: Control<ParcelFormValues>;
  readonly name: ParcelNumericFieldName;
  readonly label: string;
  readonly placeholder: string;
  readonly kind: ParcelNumericFieldKind;
  /** Acrescenta o asterisco do legado ao rótulo. */
  readonly required?: boolean | undefined;
  /** Mensagem de erro já traduzida. */
  readonly error?: string | undefined;
  /** Texto de apoio exibido quando não há erro. */
  readonly helper?: string | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * Explicação exibida em tooltip, por um ícone na linha do rótulo.
   *
   * Para o que o nome do campo não entrega sozinho. Fica em tooltip, e não em
   * `helper`, porque é texto longo: sob o campo empurraria a linha do formulário
   * e competiria com a mensagem de erro pelo mesmo espaço.
   */
  readonly info?: string | undefined;
}

/**
 * Campo numérico do formulário de criação de parcela.
 *
 * Existe como componente porque o formulário tem sete deles em três máscaras
 * diferentes, e cada um precisa da mesma dança: formatar na exibição, ler dígitos
 * na digitação e devolver `null` quando o campo fica vazio — um `0` implícito
 * gravaria desconto zero onde o operador não informou nada.
 *
 * As máscaras vêm de `maskedNumberInput`, as mesmas do formulário de plano: moeda
 * lê os dois últimos dígitos como centavos, percentual lê de frente.
 */
export function ParcelNumericField({
  control,
  name,
  label,
  placeholder,
  kind,
  required,
  error,
  helper,
  disabled,
  info,
}: ParcelNumericFieldProps) {
  const message = error ?? helper;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const numeric = typeof field.value === 'number' ? field.value : null;

        const display =
          kind === 'currency'
            ? formatCurrencyInput(numeric)
            : kind === 'percentage'
              ? formatPercentageInput(numeric)
              : numeric === null
                ? ''
                : String(numeric);

        const input = (
          <TextField
            label={required ? `${label} *` : label}
            size="md"
            fullWidth
            inputMode="numeric"
            placeholder={placeholder}
            disabled={disabled}
            hasError={error !== undefined}
            {...(message === undefined ? {} : { helper: message })}
            // `fullWidth` estica o wrapper; o `w-full` garante que o input ocupe a
            // coluna inteira do grid em vez da largura intrínseca.
            custom={{ root: 'w-full', input: 'w-full' }}
            value={display}
            onChange={event => {
              const raw = event.target.value;

              if (kind === 'currency') {
                field.onChange(parseCurrencyInput(raw));
                return;
              }
              if (kind === 'percentage') {
                field.onChange(parsePercentageInput(raw));
                return;
              }

              const digits = raw.replace(/\D/g, '');
              field.onChange(digits === '' ? null : Number(digits));
            }}
            onBlur={field.onBlur}
          />
        );

        if (info === undefined) return input;

        // O `label` do `TextField` do ADS é `string`, então o ícone não cabe
        // dentro dele: ele é ancorado no canto direito da linha do rótulo, que é
        // onde o padrão da feature já põe tooltips de campo.
        return (
          <div className="relative w-full">
            {/* `right-none`/`top-none` e não `-0`: a escala `spacing` do config
                substitui a do Tailwind, então `right-0` não gera CSS. */}
            <span className="absolute right-none top-none z-10">
              <Tooltip title={info}>
                <InformationCircleIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
              </Tooltip>
            </span>
            {input}
          </div>
        );
      }}
    />
  );
}
